import React, { useMemo } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { getOrgTermsContent } from '../../../../content/utils.js';
import SubHeader from '../../../SubHeader.jsx';
import MilestoneCard from './MilestoneCard.jsx';
import LinkedFileCard from './LinkedFileCard.jsx';
import QuestionBlueIcon from '../../../icons/QuestionBlueIcon.jsx';
import CustomTooltip from '../../../CustomTooltip.jsx';
import OverviewTextField from '../../../OverviewTextField.jsx';
import styles from './ChangeStrategyOverview.module.css';

const ChangeStrategyOverview = (props) => {
  const { changeStrategy, project } = props;

  const renderFields = useMemo(() => {
    if (changeStrategy) {
      return project.changeStrategyFields.map((field, index) => (
        <OverviewTextField
          key={field.key}
          isFieldWithEditor={project.changeStrategyFields.some(
            (item) => item.editor && item.key === field.key
          )}
          entity={changeStrategy}
          field={field}
          index={index}
        />
      ));
    }
  }, [project, changeStrategy]);

  return (
    <div
      className="component-bg-white component-xl-padding-vertical component-no-horizontal-padding"
      style={{ minHeight: '80vh' }}
    >
      {changeStrategy && (
        <>
          <Col xs={3} className={styles.profileLeftBlock}>
            <div className={`${styles.stakeholderProfileAvatar} m-b-24`}>
              <img
                src={
                  changeStrategy &&
                  !changeStrategy.image.includes('default_strategy.png')
                    ? changeStrategy.image
                    : '../../../../static/images/default-strategy.jpg'
                }
                alt="change-strategy-avatar"
                sizes="300x290"
              />
            </div>
            <h4
              className="column-header m-b-16"
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <p className="m-0">{getOrgTermsContent('projectMilestones')}</p>
              <CustomTooltip
                placement="right"
                tooltip={
                  <div style={{ maxWidth: '256px' }}>
                    <strong>
                      <h5
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          lineHeight: '20px',
                          color: '#153142',
                        }}
                        className="m-b-8"
                      >
                        {getOrgTermsContent('projectMilestones')}
                      </h5>
                    </strong>
                    <p
                      style={{
                        fontSize: '12px',
                        fontWeight: '400',
                        lineHeight: '20px',
                        color: '#7F8FA4',
                      }}
                      className="m-b-0 text-muted"
                    >
                      {getOrgTermsContent('projectMilestoneDescription')}
                    </p>
                  </div>
                }
                id="tooltip"
              >
                <Button
                  aria-label="View Support for Change modal"
                  type="button"
                  className="flex flex-align-center m-l-8 btn-last-update-icon"
                >
                  <QuestionBlueIcon />
                </Button>
              </CustomTooltip>
            </h4>
            {changeStrategy.milestones.length > 0 ? (
              <strong className="bordered text-muted">
                Total:&nbsp;
                <span className="count" style={{ color: '#27B0E0' }}>
                  {changeStrategy.milestones.length}
                </span>
              </strong>
            ) : null}
            {changeStrategy.milestones.length ? (
              changeStrategy.milestones.map((milestone) => (
                <MilestoneCard
                  key={`mile-${milestone.id}`}
                  projectMilestone
                  {...milestone}
                />
              ))
            ) : (
              <p>
                There are no {getOrgTermsContent('project')} Milestones at this
                time.
              </p>
            )}
          </Col>
          <Col xs={9} className={styles.profileRightBlock}>
            <h2 className="column-header m-b-16">Strategy Details</h2>
            <Row className="flex flex-wrap flex-align-start">
              {changeStrategy ? renderFields : null}
            </Row>
            <h2 className="column-header m-b-16">Governance</h2>
            <Row className="flex flex-wrap flex-align-start m-b-32">
              <Col xs="4">
                <div className="m-b-12">
                  <h3 className="text-muted" style={{ fontSize: '20px' }}>
                    Owner(s)
                  </h3>
                </div>
                {changeStrategy.owners.length
                  ? changeStrategy.owners.map((owner) => (
                      <p
                        key={owner.name}
                        className={`${owner.archived ? 'text-muted' : ''}`}
                      >
                        {owner.name}
                      </p>
                    ))
                  : '—'}
              </Col>
              <Col xs="4">
                <div className="m-b-12">
                  <h3
                    className="text-muted m-b-12"
                    style={{ fontSize: '20px' }}
                  >
                    Steering Committee
                  </h3>
                </div>
                {changeStrategy.steering_committee.length
                  ? changeStrategy.steering_committee.map((commit) => (
                      <p
                        key={commit.name}
                        className={commit.archived ? 'text-muted' : ''}
                      >
                        {commit.name}
                      </p>
                    ))
                  : '—'}
              </Col>
              <Col xs="4">
                <div className="m-b-12">
                  <h3
                    className="text-muted m-b-12"
                    style={{ fontSize: '20px' }}
                  >
                    {getOrgTermsContent('projectSponsors')}
                  </h3>
                </div>
                {changeStrategy.project_sponsors.length
                  ? changeStrategy.project_sponsors.map((sponsor) => (
                      <p
                        key={sponsor.name}
                        className={sponsor.archived ? 'text-muted' : ''}
                      >
                        {sponsor.name}
                      </p>
                    ))
                  : '—'}
              </Col>
              <Col xs="4">
                <div className="m-b-12 m-t-16">
                  <h3
                    className="text-muted m-b-12"
                    style={{ fontSize: '20px' }}
                  >
                    Supporting Resources
                  </h3>
                </div>
                {changeStrategy.supporting_resources.length
                  ? changeStrategy.supporting_resources.map((resource) => (
                      <p
                        key={resource.name}
                        className={resource.archived ? 'text-muted' : ''}
                      >
                        {resource.name}
                      </p>
                    ))
                  : '—'}
              </Col>
            </Row>
            <h2
              style={{ background: '#F7FAFD', padding: 12 }}
              className="m-b-16"
            >
              <div className="flex horizontal-label">
                <h2
                  className="horizontal-label"
                  style={{ fontSize: '20px', marginRight: '20px' }}
                >
                  Linked Files
                </h2>
                <strong
                  className="bordered text-muted"
                  style={{ fontSize: '14px' }}
                >
                  Total :&nbsp;
                  <span className="text-primary" style={{ fontSize: '14px' }}>
                    {changeStrategy.linked_files.length}
                  </span>
                </strong>
              </div>
            </h2>
            {changeStrategy.linked_files.length ? (
              <Row
                style={{
                  padding: '10px 14px',
                  borderBottom: '1px solid #DFE2E5',
                }}
              >
                <Col xs="4">
                  <p className="text-muted">File Name</p>
                </Col>
                <Col xs="3">
                  <span className="text-muted">Uploaded By</span>
                </Col>
                <Col xs="3">
                  <span className="text-muted">Who Can Access</span>
                </Col>
                <Col xs="2">
                  <p className="text-muted">Date Uploaded</p>
                </Col>
              </Row>
            ) : null}
            {changeStrategy.linked_files.length ? (
              changeStrategy.linked_files.map((link) => (
                <LinkedFileCard key={`link-${link.id}`} showAccess {...link} />
              ))
            ) : (
              <p>
                There are no Linked Files at this time. Linked Files allow you
                to add supporting documentation and materials to the Change
                Strategy page.
              </p>
            )}
          </Col>
        </>
      )}
    </div>
  );
};

export default ChangeStrategyOverview;
